import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  ChangeDetectionStrategy,
  AfterViewInit,
  OnDestroy,
  ChangeDetectorRef,
  ViewEncapsulation
} from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { takeUntil, flatMap, take, finalize } from 'rxjs/operators';
import { Select, Store, Actions, ofActionDispatched } from '@ngxs/store';
import { ViewState } from '../../../state/view.state';
import { ViewService } from '../../../view.service';
import { TakeChapterPicture, TakeChapterPictureSuccess } from 'src/app/story/state/chapters/story.chapters.actions';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { ImageTypeEnum } from 'src/app/shared/models/image-type.enum';
import { ScreenDimensions } from 'src/app/shared/models/screen.dimensions.model';
import { StoryState } from 'src/app/story/state/story.state';
import { LayoutActions } from 'src/app/layout/state/layout.actions';
import { SiteHeaderType } from 'src/app/layout/models/site-header-type.enum';
import { LayoutState } from 'src/app/layout/state/layout.state';
import { WhiteboardAnnotateMode } from '../../../models/whiteboard-state.model';
import { ViewActions } from '../../../state/view.actions';
import { uniqueId } from 'lodash';
import { ExportImageUtils } from 'src/app/shared/libraries/image/export-image';
import { environment } from 'src/environments/environment';
import { ClipboardService } from 'src/app/shared/clipboard.service';
import { EnvironmentPlatform } from 'src/environments/models/environment-platform';

// export defaults
const exportFormat: ImageTypeEnum = ImageTypeEnum.PNG;
const exportQuality = 100;

interface LabelDescriptor {
  id: string;
  pos: {x: number, y: number};
  color: string;
  size: number;
}

@Component({
  selector: 'app-view-annotate',
  templateUrl: './view-annotate.component.html',
  styleUrls: ['./view-annotate.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class ViewAnnotateComponent implements OnInit, AfterViewInit, OnDestroy {
  whiteboardAnnotateMode = WhiteboardAnnotateMode;

  @ViewChild('drawCanvas', { static: true })
  drawCanvas: ElementRef;

  @ViewChild('annotateWrapper', { static: true })
  annotateElement: ElementRef;

  @Select(ViewState.getWhiteboardAnnotateMode)
  annotateMode$: Observable<WhiteboardAnnotateMode>;

  @Select(ViewState.getWhiteboardBrushSize)
  brushSize$: Observable<number>;

  @Select(ViewState.getWhiteboardBrushColor)
  brushColor$: Observable<string>;

  @Select(ViewState.getWhiteboardTextSize)
  textSize$: Observable<number>;

  @Select(ViewState.getWhiteboardEraserSize)
  eraserSize$: Observable<number>;

  siteHeader: SiteHeaderType;
  labels: LabelDescriptor[] = [];
  activeLabelId: string;
  exporting = false;

  private inputManager: HammerManager;
  private canvasElement: HTMLCanvasElement;
  private canvasContext: CanvasRenderingContext2D;

  private brushSize = 0;
  private eraserSize = 0;
  private lineWidth = 0;

  private ngUnsubscribe = new Subject();

  constructor(
    private store: Store,
    private actions: Actions,
    private viewService: ViewService,
    private clipboardService: ClipboardService,
    private changeRef: ChangeDetectorRef
  ) {}

  initTextModeListener() {
    this.inputManager = new Hammer.Manager(this.drawCanvas.nativeElement);
    this.inputManager.add(new Hammer.Tap({ event: 'tap' }));
    this.inputManager.on('tap', (event) => {
      if (this.activeLabelId) {
        this.activeLabelId = undefined;
        return;
      }

      const labelId = uniqueId();
      let x = event.center.x;
      let y = event.center.y;
      if ( event.pointers[0].x !== undefined) {
        x = event.pointers[0].x;
        y = event.pointers[0].y;
      }

      const color = this.store.selectSnapshot(
        ViewState.getWhiteboardBrushColor
      );
      const size = this.store.selectSnapshot(ViewState.getWhiteboardTextSize);

      this.labels.push({
        id: labelId,
        pos: { x, y },
        color,
        size,
      });
      this.activeLabelId = labelId;
      this.changeRef.markForCheck();
      this.dispatchHasText();
    });
  }

  handleLabelActive(activeLabelId) {
    this.activeLabelId = activeLabelId;
    const activeLabel = this.labels.find(label => label.id === activeLabelId);
    if (activeLabel)  {
      this.store.dispatch([
        new ViewActions.SetWhiteboardTextSize(activeLabel.size),
        new ViewActions.SetWhiteboardBrushColor(activeLabel.color)
      ]);
    }
  }

  handleLabelEmpty(labelId) {
    this.labels = this.labels.filter(label => label.id !== labelId);
    this.changeRef.markForCheck();
    this.dispatchHasText();
  }

  dispatchHasText() {
    this.store.dispatch(new ViewActions.SetWhiteboardHasText(
      this.labels.length > 0
    ));
  }

  ngOnInit() {
    this.canvasElement = this.drawCanvas.nativeElement;
    this.canvasContext = this.canvasElement.getContext('2d');

    this.siteHeader = this.store.selectSnapshot(LayoutState.getSiteHeader);
    this.store.dispatch(new LayoutActions.SetHeader(SiteHeaderType.Hide));


    this.brushColor$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(color => {
      const activeLabel = this.labels.find(label => label.id === this.activeLabelId);
      if (activeLabel) {
        activeLabel.color = color;
        this.changeRef.markForCheck();
      }
    });

    this.textSize$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(size => {
      const activeLabel = this.labels.find(label => label.id === this.activeLabelId);
      if (activeLabel) {
        activeLabel.size = size;
        this.changeRef.markForCheck();
      }
    });

    this.annotateMode$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((mode) => {

      if (this.inputManager) {
        this.inputManager.destroy();
      }

      if (mode === WhiteboardAnnotateMode.Paint) {
        
        this.canvasContext.globalCompositeOperation = 'source-over';
        this.setLineWidth(this.brushSize);
        this.initInputListeners();
      } else if (mode === WhiteboardAnnotateMode.Text) {
        
        this.initTextModeListener();
      } else if (mode === WhiteboardAnnotateMode.Erase) {

        this.canvasContext.globalCompositeOperation = 'destination-out';
        this.setLineWidth(this.eraserSize);
        this.initInputListeners();
      }
    });
  }

  ngAfterViewInit() {
    this.initCanvasListeners();
  }

  ngOnDestroy() {
    // cleanup input handling
    this.inputManager.destroy();

    // cleanup canvas
    this.reset();

    // reset header
    this.store.dispatch(new LayoutActions.SetHeader(this.siteHeader));

    // cleanup subscriptions
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  initCanvasListeners() {
    // set canvas pen settings
    this.brushSize$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((brushSize) => {
        this.setBrushSize(brushSize);
      });

    this.brushColor$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((brushColor) => {
        this.setBrushColor(brushColor);
      });
    
    this.eraserSize$
    .pipe(takeUntil(this.ngUnsubscribe))
    .subscribe((eraserSize) => {
      this.setEraserSize(eraserSize);
    });

    // set initial state
    this.syncCanvas();
  }

  initInputListeners() {
    // Input Handlers
    this.inputManager = new Hammer.Manager(this.drawCanvas.nativeElement);
    this.inputManager.add(new Hammer.Pan({ event: 'pan', pointers: 0 }));
    this.inputManager.add(new Hammer.Tap({ event: 'tap' }));
    /*
      NOTE: Hammerjs doesn't track delta movements (i.e., movementX) in subpixel,
            causing jagged edges in drawing lines (slight offset).
            Track last movements manually to get smooth drawing.
    */
    const lastPoints = new Map<number, { x: number; y: number }>();

    // draw point
    this.inputManager.on('tap', (event) => {
      event.pointers.forEach((pointer: PointerEvent) => {
        if (pointer.x !== undefined) {
          const { x, y } = pointer;
          this.drawPoint(x, y);
        } else {
          const { x, y } = event.center;
          this.drawPoint(x, y);
        }
        this.checkCanvasPaint();
      });
    });

    // draw start: set initial point tracking
    this.inputManager.on('panstart', (event) => {
      event.pointers.forEach((pointer: PointerEvent) => {
        if (pointer.x === undefined) {
          lastPoints.set(1, event.center);
        } else {
          const { x, y } = pointer;
          lastPoints.set(pointer.pointerId, { x, y });
        }
      });
    });

    // draw: line from -> to
    this.inputManager.on('pan', (event) => {
      event.pointers.forEach((pointer: PointerEvent) => {
        let x = event.center.x;
        let y = event.center.y;
        let pointerId = 1;
        if (pointer.pointerId !== undefined) {
          x = pointer.x;
          y = pointer.y;
          pointerId = pointer.pointerId;
        }
        const { x: lastX, y: lastY } = lastPoints.get(pointerId);
        
        // draw line from last point
        this.drawLine(lastX, lastY, x, y);
        // draw circle at current point
        this.drawPoint(x, y);
        // update last movement
        lastPoints.set(pointerId, { x, y });
      });
    });

    // draw end: snapshot current canvas
    this.inputManager.on('panend', () => {
      lastPoints.clear();
      this.checkCanvasPaint();
    });
  }

  handleResetClick() {
    this.reset();
  }

  handleDownloadClick() {
    // Force changeref detection right away so the UI updates before snapshot
    this.exporting = true;
    this.changeRef.detectChanges();


    const { width, height } = this.canvasElement;
    const chapterId = this.store.selectSnapshot(
      StoryChaptersState.getSelectedChapterId
    );

    // create snapshot with drawing canvas
    this.store
      .dispatch(
        new TakeChapterPicture(
          chapterId,
          exportFormat,
          exportQuality,
          { width, height },
          'original',
          true,
          true,
          [this.annotateElement.nativeElement]
        )
      )
      .pipe(
        flatMap(() => {
          return this.actions.pipe(
            ofActionDispatched(TakeChapterPictureSuccess),
            take(1)
          );
        }),
        takeUntil(this.ngUnsubscribe),
        finalize(() => {
          this.exporting = false;
          this.changeRef.markForCheck();
        })
      )
      .subscribe((response: TakeChapterPictureSuccess) => {
        this.downloadSnapshot(response.blob);
      });
  }

  handleExitClick() {
    this.resetCanvas();
    this.viewService.toggleWhiteboard(false);
  }

  private checkCanvasPaint() {
    const imageData = this.canvasContext.getImageData(
      0, 0, this.canvasElement.width, this.canvasElement.height
    );

    const pixelBuffer = new Uint32Array(imageData.data.buffer);
    const hasPaint = pixelBuffer.some(color => color !== 0);
    
    this.store.dispatch(new ViewActions.SetWhiteboardHasPaint(hasPaint));
  }

  // draw point
  private drawPoint(toX: number, toY: number) {
    const radius = this.lineWidth / 2;
    const angle = 2 * Math.PI;
    this.canvasContext.beginPath();
    this.canvasContext.arc(toX, toY, radius, 0, angle);
    this.canvasContext.fill();
  }

  // draw line from start to end
  private drawLine(fromX: number, fromY: number, toX: number, toY: number) {
    this.canvasContext.beginPath();
    this.canvasContext.moveTo(fromX, fromY);
    this.canvasContext.lineTo(toX, toY);
    this.canvasContext.stroke();
    this.canvasContext.closePath();
  }

  private reset() {
    this.resetCanvas();
    this.labels = [];
    this.changeRef.markForCheck();
    this.dispatchHasText();
  }

  private setBrushSize(brushSize: number) {
    this.brushSize = brushSize;
    this.setLineWidth(brushSize);
  }

  private setBrushColor(brushColor: string) {
    this.canvasContext.fillStyle = brushColor;
    this.canvasContext.strokeStyle = brushColor;
  }

  private setEraserSize(eraserSize: number) {
    this.eraserSize = eraserSize;
    this.setLineWidth(eraserSize);
  }

  private setLineWidth(lineWidth: number) {
    this.canvasContext.lineWidth = lineWidth;
    this.lineWidth = lineWidth;
  }

  // reset canvas params on state change
  private syncCanvas() {
    const brushSize = this.store.selectSnapshot(
      ViewState.getWhiteboardBrushSize
    );

    const brushColor = this.store.selectSnapshot(
      ViewState.getWhiteboardBrushColor
    );

    const eraserSize = this.store.selectSnapshot(
      ViewState.getWhiteboardEraserSize
    );

    // size canvas sizing
    const { width, height } = this.getScreenDimensions();
    this.canvasElement.width = width;
    this.canvasElement.height = height;

    // set canvas tools
    this.setBrushSize(brushSize);
    this.setBrushColor(brushColor);
    this.setEraserSize(eraserSize);

    this.canvasContext.lineJoin = 'round';
  }

  // clear canvas drawing
  private resetCanvas() {
    const { width, height } = this.canvasElement;
    this.canvasContext.clearRect(0, 0, width, height);
    
    this.store.dispatch(new ViewActions.SetWhiteboardHasPaint(false));
  }

  private downloadSnapshot(blob: Blob) {
    const { displayName } = this.store.selectSnapshot(StoryState.getStory);
    const normalizedTitle = (displayName || '')
      .replace(/ /g, '_')
      .replace(/\W/g, '')
      .toLowerCase();
    const extension = exportFormat === ImageTypeEnum.PNG ? 'png' : 'jpg';
    const fileName = `${normalizedTitle}_drawing.${extension}`;

    if (environment.native === true || environment.platform === EnvironmentPlatform.SDK) { // share
      ExportImageUtils.blobToBase64(blob)
        .pipe(
          // native copy
          flatMap((base64Image) => this.clipboardService.copyToClipboard(base64Image, 'image')),
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe((copied) => { });
    } else { // download
      ExportImageUtils.exportImage(blob, exportFormat, fileName);
    }
  }

  private getScreenDimensions(): ScreenDimensions {
    const { innerWidth, innerHeight } = window;
    return {
      width: innerWidth,
      height: innerHeight,
    };
  }
}
